import { ChangeEvent, CSSProperties, FC } from "react";
import { Button, makeStyles, useMediaQuery } from "@material-ui/core";
import {
  Pagination as PaginationMUI,
  PaginationItem,
  PaginationRenderItemParams,
} from "@material-ui/lab";
import { includes, upperFirst } from "lodash";

import theme from "theme";

const useStyles = makeStyles(theme => {
  const paginationUlCommonStyles = {
    "& li": {
      backgroundColor: "unset",
      border: "unset",
      cursor: "unset",
      padding: "unset",
      marginTop: theme.spacing(2),
    },
  };
  return {
    paginationDesktop: {
      "& .MuiPagination-ul": {
        ...paginationUlCommonStyles,
        display: "flex",
        justifyContent: "center",
      },
      marginBottom: theme.spacing(2),
    },
    paginationMobileNextPreviousControls: {
      "& .MuiPagination-ul": {
        display: "flex",
        justifyContent: "center",
        "& li": {
          ...paginationUlCommonStyles["& li"],
          padding: 0,
          width: "auto",
          margin: "auto",
          flexGrow: 1,
          flexBasis: "40%",
        },
        "& li:first-child": {
          marginRight: theme.spacing(0.5),
        },
        "& li:last-child": {
          marginLeft: theme.spacing(0.5),
        },
        "& li:not(:first-child):not(:last-child)": {
          display: "none",
        },
      },
    },
    paginationMobilePageNumberSelect: {
      display: "flex",
      justifyContent: "center",
      "& .MuiPagination-ul": {
        ...paginationUlCommonStyles,
        margin: "auto",
      },
      marginBottom: theme.spacing(2),
    },
  };
});

const PaginationItemDisableCurrentPage: FC<{
  currentPage: number;
  item: PaginationRenderItemParams;
}> = ({ currentPage, item }) => (
  <PaginationItem
    {...item}
    disabled={item.disabled || item.page === currentPage}
  />
);

const PositionedSpan: FC<{ direction: "left" | "right" }> = ({ direction }) => {
  let glyph = "";
  const style: CSSProperties = { marginBottom: "3px" };
  if (direction === "left") {
    glyph = "←";
    style["marginRight"] = "8px";
  } else {
    glyph = "→";
    style["marginLeft"] = "8px";
  }

  return <span style={style}>{glyph}</span>;
};

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onChange: (event: ChangeEvent<unknown>, page: number) => void;
}

const Pagination: FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onChange,
}) => {
  const classes = useStyles(theme);
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  return (
    <>
      {isDesktop ? (
        <PaginationMUI
          className={classes.paginationDesktop}
          count={totalPages}
          page={currentPage}
          onChange={onChange}
          showFirstButton
          showLastButton
          renderItem={item => (
            <PaginationItemDisableCurrentPage
              currentPage={currentPage}
              item={item}
            />
          )}
        />
      ) : (
        <>
          {/* Renders the Previous/Next controls */}
          <PaginationMUI
            className={classes.paginationMobileNextPreviousControls}
            count={totalPages}
            renderItem={item => {
              const skipRenderTypes = [
                "page",
                "start-ellipsis",
                "end-ellipsis",
              ];
              return !includes(skipRenderTypes, item.type) ? (
                <Button
                  fullWidth
                  variant="outlined"
                  size="large"
                  onClick={item.onClick}
                  disabled={item.disabled}
                  style={{
                    backgroundColor: theme.custom.palette.gray.light,
                    textTransform: "none",
                    cursor: !item.disabled ? "pointer" : "unset",
                  }}
                >
                  {item.type === "previous" && (
                    <PositionedSpan direction="left" />
                  )}
                  {upperFirst(item.type)}
                  {item.type === "next" && <PositionedSpan direction="right" />}
                </Button>
              ) : null;
            }}
            page={currentPage}
            onChange={onChange}
          />
          {/* Renders the Page Number selection controls */}
          <PaginationMUI
            className={classes.paginationMobilePageNumberSelect}
            count={totalPages}
            hideNextButton
            hidePrevButton
            page={currentPage}
            onChange={onChange}
            renderItem={item => (
              <PaginationItemDisableCurrentPage
                currentPage={currentPage}
                item={item}
              />
            )}
          />
        </>
      )}
    </>
  );
};

export default Pagination;
